import { useCallback, useEffect, useRef } from 'react';

const DEAD_ZONE = 10;
const DIRECTION_RATIO = 1.5;
const MIN_DISTANCE = 50;
const FAST_FLICK_DISTANCE = 30;
const MIN_VELOCITY = 0.3;
const MAX_DURATION = 500;
const SNAP_THRESHOLD = 0.5; // 50% of viewport to snap to next tab
const ANIMATION_MS = 100;

/** Check if target sits inside a horizontally-scrollable ancestor (before container). */
function hasScrollableAncestor(target, container) {
    let el = target;
    while (el && el !== container) {
        if (el.scrollWidth > el.clientWidth) {
            const style = getComputedStyle(el);
            if (style.overflowX === 'auto' || style.overflowX === 'scroll') return true;
        }
        el = el.parentElement;
    }
    return false;
}

/**
 * Finger-tracking horizontal swipe between tab panels.
 *
 * During touch: imperatively drives translateX on the slider element.
 * On release: snaps to the nearest tab with a CSS transition.
 * animateToTab(): programmatic transition (for button presses).
 */
export function useTabSwipe({
    sliderRef,
    containerRef,
    activeTab,       // numeric index (0, 1, ...)
    onTabChange,     // (tabIndex: number) => void
    tabCount = 2,
    enabled = true,
}) {
    const stateRef = useRef({
        direction: null,   // null | 'h' | 'v'
        startX: 0,
        startY: 0,
        startTime: 0,
        baseOffset: 0,
        viewportWidth: 0,
        animating: false,
    });

    // Keep activeTab accessible without re-registering listeners
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;

    const onTabChangeRef = useRef(onTabChange);
    onTabChangeRef.current = onTabChange;

    // Set transform without triggering React re-render
    const setTransform = useCallback((px) => {
        const el = sliderRef.current;
        if (el) el.style.transform = `translateX(${px}px)`;
    }, [sliderRef]);

    // Snap to a tab with CSS transition
    const animateToTab = useCallback((targetTab, durationMs = ANIMATION_MS) => {
        const el = sliderRef.current;
        const container = containerRef.current;
        if (!el || !container) return;

        const vw = container.offsetWidth;
        const targetX = -targetTab * vw;
        const state = stateRef.current;

        // If already at the target, just update state
        const currentTransform = el.style.transform;
        const match = currentTransform?.match(/translateX\((-?\d+(?:\.\d+)?)px\)/);
        const currentX = match ? parseFloat(match[1]) : -activeTabRef.current * vw;

        if (Math.abs(currentX - targetX) < 1) {
            setTransform(targetX);
            onTabChangeRef.current(targetTab);
            return;
        }

        state.animating = true;
        el.style.transition = `transform ${durationMs}ms ease-out`;
        setTransform(targetX);

        const timerId = setTimeout(() => {
            el.removeEventListener('transitionend', onEnd);
            el.style.transition = '';
            state.animating = false;
            onTabChangeRef.current(targetTab);
        }, durationMs + 50);

        const onEnd = (e) => {
            if (e.propertyName !== 'transform') return;
            clearTimeout(timerId);
            el.removeEventListener('transitionend', onEnd);
            el.style.transition = '';
            state.animating = false;
            onTabChangeRef.current(targetTab);
        };
        el.addEventListener('transitionend', onEnd);
    }, [sliderRef, containerRef, setTransform]);

    // Sync transform when activeTab changes externally (e.g. from BottomNav)
    useEffect(() => {
        const el = sliderRef.current;
        const container = containerRef.current;
        if (!el || !container) return;
        if (stateRef.current.animating) return; // don't interfere with in-progress animation
        const vw = container.offsetWidth;
        setTransform(-activeTab * vw);
    }, [activeTab, sliderRef, containerRef, setTransform]);

    // Handle resize: update transform to match current tab
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => {
            if (stateRef.current.animating) return;
            const vw = container.offsetWidth;
            setTransform(-activeTabRef.current * vw);
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [containerRef, setTransform]);

    // Touch event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !enabled) return;

        const state = stateRef.current;

        function onTouchStart(e) {
            if (state.animating) {
                // Cancel in-progress animation
                const el = sliderRef.current;
                if (el) {
                    const computed = getComputedStyle(el);
                    const matrix = new DOMMatrix(computed.transform);
                    el.style.transition = '';
                    el.style.transform = `translateX(${matrix.m41}px)`;
                    state.animating = false;
                }
            }

            const touch = e.touches[0];
            state.direction = null;
            state.startX = touch.clientX;
            state.startY = touch.clientY;
            state.startTime = Date.now();
            state.viewportWidth = container.offsetWidth;
            state.baseOffset = -activeTabRef.current * state.viewportWidth;
            state.insideScrollable = hasScrollableAncestor(e.target, container);
        }

        function onTouchMove(e) {
            if (state.direction === 'v') return;

            const touch = e.touches[0];
            const dx = touch.clientX - state.startX;
            const dy = touch.clientY - state.startY;
            const absDx = Math.abs(dx);
            const absDy = Math.abs(dy);

            if (state.direction === null) {
                if (absDx < DEAD_ZONE && absDy < DEAD_ZONE) return;
                // Yield horizontal gestures to scrollable children
                if (state.insideScrollable && absDx > absDy) {
                    state.direction = 'v';
                    return;
                }
                state.direction = absDx > absDy * DIRECTION_RATIO ? 'h' : 'v';
                if (state.direction === 'v') return;
            }

            e.preventDefault();

            const maxOffset = 0;
            const minOffset = -(tabCount - 1) * state.viewportWidth;
            let rawOffset = state.baseOffset + dx;

            // Rubber-band at edges
            if (rawOffset > maxOffset) {
                rawOffset = maxOffset + (rawOffset - maxOffset) * 0.3;
            } else if (rawOffset < minOffset) {
                rawOffset = minOffset + (rawOffset - minOffset) * 0.3;
            }

            setTransform(rawOffset);
        }

        function onTouchEnd(e) {
            if (state.direction !== 'h') return;

            const touch = e.changedTouches[0];
            const dx = touch.clientX - state.startX;
            const elapsed = Date.now() - state.startTime;
            const absDx = Math.abs(dx);
            const velocity = absDx / elapsed;

            let targetTab = activeTabRef.current;

            // Flick detection
            if (velocity >= MIN_VELOCITY && absDx >= FAST_FLICK_DISTANCE && elapsed <= MAX_DURATION) {
                targetTab += dx < 0 ? 1 : -1;
            }
            // Distance threshold: check how far past the midpoint
            else {
                const progress = -dx / state.viewportWidth;
                if (Math.abs(progress) >= SNAP_THRESHOLD) {
                    targetTab += progress > 0 ? 1 : -1;
                }
            }

            // Clamp
            targetTab = Math.max(0, Math.min(tabCount - 1, targetTab));

            // Animate to target
            const distance = Math.abs((-targetTab * state.viewportWidth) - (state.baseOffset + dx));
            const duration = Math.max(80, Math.min(200, distance / (velocity || 0.5)));
            animateToTab(targetTab, Math.round(duration));
        }

        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false });
        container.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
        };
    }, [enabled, sliderRef, containerRef, tabCount, setTransform, animateToTab]);

    return { animateToTab };
}
